import { Injectable, DOCUMENT, inject, signal } from '@angular/core';

import { isHexColor, tenantPalette, type TenantPalette } from './branding';
import type { TenantBrandingWire } from './portal-models';

/**
 * Runtime tenant branding: reads the company's branding off the positions payload and writes
 * CSS custom properties on the document root, so every rk-* component re-tints from one place
 * (docs/10 4a tenant-branded portal; A1 white-label is a later feature - until the payload
 * carries colors, nothing is written and the rekruto token defaults hold).
 *
 * Every value is validated before it reaches a CSS custom property (#RRGGBB only, mirroring
 * TenantBranding's server-side validation): an invalid or missing value is ignored, never
 * substituted into the document.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly document = inject(DOCUMENT);

  /** Company display name from the latest portal payload; drives the top bar. */
  readonly companyName = signal('');

  /** True once a tenant palette has been applied (the shell scopes overrides on this flag). */
  readonly branded = signal(false);

  apply(companyName: string, branding?: TenantBrandingWire | null): void {
    this.companyName.set(companyName);
    this.resetPalette();

    const primary = branding?.primaryColor;
    if (!isHexColor(primary)) return;

    const palette: TenantPalette = tenantPalette(primary);
    const root = this.document.documentElement;
    root.style.setProperty('--rk-tenant-primary', palette.primary);
    root.style.setProperty('--rk-tenant-primary-hover', palette.primaryHover);
    root.style.setProperty('--rk-tenant-on-primary', palette.onPrimary);
    root.style.setProperty('--rk-tenant-text-light', palette.textOnLight);
    root.style.setProperty('--rk-tenant-text-dark', palette.textOnDark);
    this.branded.set(true);
  }

  /** Removes any applied tenant palette; the tokens fall back to their rekruto defaults. */
  reset(): void {
    this.companyName.set('');
    this.resetPalette();
  }

  private resetPalette(): void {
    const root = this.document.documentElement;
    root.style.removeProperty('--rk-tenant-primary');
    root.style.removeProperty('--rk-tenant-primary-hover');
    root.style.removeProperty('--rk-tenant-on-primary');
    root.style.removeProperty('--rk-tenant-text-light');
    root.style.removeProperty('--rk-tenant-text-dark');
    this.branded.set(false);
  }
}
